import classNames from 'classnames';
import {useMediaQuery} from 'hooks/useMediaQuery';
import {breakpoints} from 'lib/breakpoints';
import {validatePhoneNumber} from 'lib/validate';
import {getLkBasename} from 'modules/PersonalAccount/lib/getLkBasename';
import {Link, useInRouterContext} from 'react-router';

import {Button, ButtonSize, ButtonType} from 'components/Button/Button';
import {Icon} from 'components/Icon/Icon';
import {PhoneConfirmation} from 'components/PhoneConfirmation/PhoneConfirmation';
import {SignUpLinksModal} from 'components/SignUpLinksModal/SignUpLinksModal';
import {Switch, SwitchTheme} from 'components/Switch/Switch';
import {Text} from 'components/Text/Text';
import {TextFieldType} from 'components/TextField/TextField';
import {TextFieldHookForm} from 'components/TextFieldHookForm/TextFieldHookForm';

import styles from './SignInForm.module.css';
import {useSignInForm} from './useSignInForm';

type Props = {
    onSignInSuccess?: () => void;
    className?: string;
};

export const SignInForm: React.FC<Props> = ({onSignInSuccess, className}) => {
    const {
        tabs,
        selectedTab,
        setSelectedTab,
        control,
        handleSubmit,
        formIsValid,
        isCompanyTab,
        phoneModalIsVisible,
        togglePhoneModal,
        phone,
        phoneIsTouched,
        handleRequestPhoneVerifyError,
        handleRequestPhoneVerifySuccess,
        handleVerifySmsError,
        handleVerifySmsSuccess,
        shouldShowErrorTooltipFields,
        verifyPhoneIsPending,
        signupModalIsVisible,
        toggleSignupModal,
    } = useSignInForm({onSignInSuccess});

    const inRouterProvider = useInRouterContext();
    const LinkComponent = inRouterProvider ? Link : 'a';

    const isDesktop = useMediaQuery(breakpoints.up('lg'));
    const TitleComponent = isDesktop ? Text.Bold24 : Text.Bold20;

    const shouldShowSignUpSection = true;

    return (
        <div className={classNames(styles.block, className)}>
            <div className={styles.column}>
                <TitleComponent className={styles.title}>
                    Войти в личный кабинет
                </TitleComponent>
                <Switch
                    onChange={setSelectedTab}
                    switches={tabs}
                    selectedSwitchId={selectedTab}
                    theme={SwitchTheme.Dark}
                    className={styles.switch}
                    classNameItem={styles.switchItem}
                />
                <form onSubmit={handleSubmit} className={styles.form}>
                    {isCompanyTab && (
                        <TextFieldHookForm
                            label="ИНН"
                            name="companyInn"
                            control={control}
                            type={TextFieldType.DarkBgFill}
                            className={styles.input}
                            required
                            hideErrorTooltip={
                                !shouldShowErrorTooltipFields.companyInn
                            }
                        />
                    )}

                    <TextFieldHookForm
                        label="Телефон"
                        name="phone"
                        control={control}
                        type={TextFieldType.DarkBgFill}
                        className={styles.input}
                        mask="+7 999 999 99 99"
                        validate={
                            phoneIsTouched ? validatePhoneNumber : undefined
                        }
                        required
                        hideErrorTooltip={!shouldShowErrorTooltipFields.phone}
                    />

                    <Button
                        type={ButtonType.Primary}
                        size={ButtonSize.L}
                        className={styles.button}
                        submit
                        disabled={!formIsValid || verifyPhoneIsPending}
                    >
                        Войти
                    </Button>

                    {Boolean(localStorage.getItem('lkDealers')) && (
                        <a
                            href={`${getLkBasename()}/signin/dealers`}
                            className={styles.dealersEnter}
                        >
                            <Icon name="entrance" />
                            <Text.Semibold14>Вход для дилеров</Text.Semibold14>
                        </a>
                    )}
                </form>
            </div>

            {shouldShowSignUpSection && (
                <div className={styles.column}>
                    <TitleComponent className={styles.title}>
                        Еще не зарегистрированы?
                    </TitleComponent>

                    <Text.Semibold14 className={styles.subtitle}>
                        Если вы наш клиент, то личный кабинет откроет для вас
                        множество полезных опций
                    </Text.Semibold14>

                    <LinkComponent
                        to="/signup/company"
                        href={`/lk/signup/company`}
                        className={styles.signUpButton}
                    >
                        <div className={styles.signUpButtonIconBlock}>
                            <Icon name="company" />
                        </div>
                        <Text.Bold14>
                            Зарегистрировать компанию или ИП
                        </Text.Bold14>
                    </LinkComponent>

                    <LinkComponent
                        to="/signup/individual"
                        href={`/lk/signup/individual`}
                        className={styles.signUpButton}
                    >
                        <div className={styles.signUpButtonIconBlock}>
                            <Icon name="account" />
                        </div>
                        <Text.Bold14>
                            Зарегистрироваться как физлицо
                        </Text.Bold14>
                    </LinkComponent>
                </div>
            )}

            <PhoneConfirmation
                visible={phoneModalIsVisible}
                onBack={togglePhoneModal}
                phone={phone}
                onRequestPhoneVerifyError={handleRequestPhoneVerifyError}
                onRequestPhoneVerifySuccess={handleRequestPhoneVerifySuccess}
                onVerifySmsError={handleVerifySmsError}
                onVerifySmsSuccess={handleVerifySmsSuccess}
            />

            <SignUpLinksModal
                visible={signupModalIsVisible}
                onClose={toggleSignupModal}
            />
        </div>
    );
};
